import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { isNumber, isPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import PlatformConditional from '../../../components/PlatformConditional';
import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import { nsaa, hine_mm, hfmse, chop_intend, rulm, mfm } from 'neuro-schemas';
import { filterByDocumentType, sortDocumentsDescending } from 'Utility/documentHandling';
import { fm } from 'Components/FormatMessage';
import { TRelevantDocumentType } from '../utils';

type TileData = import('Components/DashboardTile/TileContentMaker/components/definitions').IData;

const sortAndTakeFirst = <T extends TDocumentType>(
  type: T,
  documents: IDocumentWithTypeV2<TRelevantDocumentType>[],
): IDocumentWithTypeV2<T> | undefined => {
  return filterByDocumentType(documents, type).sort(sortDocumentsDescending).at(0);
};

const MotorDash = ({ documents }: IOwnProps): React.JSX.Element => {
  const diagnoses = new Set(filterByDocumentType(documents, 'diagnosis').map(d => d.diagnosis));
  const hineDoc = sortAndTakeFirst('hine_mm', documents);
  const rulmDoc = sortAndTakeFirst('rulm', documents);
  const chopDoc = sortAndTakeFirst('chop_intend', documents);
  const hfmsDoc = sortAndTakeFirst('hfms_e', documents);
  const mwtDoc = diagnoses.has('G12.1') || diagnoses.has('G71.06') ? sortAndTakeFirst('mwt', documents) : undefined;
  const nsaaDoc = sortAndTakeFirst('nsaa', documents);
  const mfmDoc = sortAndTakeFirst('mfm', documents);

  const smaData: TileData[] = [];
  const dmdData: TileData[] = [];

  if (hineDoc) {
    smaData.push({
      date: isPartialDate(hineDoc.date) ? formatPartialDate(hineDoc.date) : '',
      title: fm('motor.hineMM'),
      value: hine_mm.calculateScore(hineDoc),
      key: 'hineMM',
    });
  }
  if (rulmDoc) {
    const superiorHand = rulm.utils.rulmSuperiorHand(rulmDoc);
    smaData.push({
      date: isPartialDate(rulmDoc.date) ? formatPartialDate(rulmDoc.date) : '',
      title: fm('motor.rulm'),
      // Check that at least one of the hands has some valid RULM-test score value
      value: superiorHand ? rulm.calculateScore(rulmDoc, superiorHand) : null,
      key: 'rulm',
    });
  }
  if (chopDoc) {
    smaData.push({
      date: isPartialDate(chopDoc.date) ? formatPartialDate(chopDoc.date) : '',
      title: fm('motor.chopINTEND'),
      value: chop_intend.calculateScore(chopDoc),
      key: 'chopINTEND',
    });
  }
  if (hfmsDoc) {
    smaData.push({
      date: isPartialDate(hfmsDoc.date) ? formatPartialDate(hfmsDoc.date) : '',
      title: fm('motor.hfmsE'),
      value: hfmse.calculateScore(hfmsDoc),
      key: 'hfmsE',
    });
  }

  if (mwtDoc) {
    smaData.push({
      date: isPartialDate(mwtDoc.date) ? formatPartialDate(mwtDoc.date) : '',
      title: fm('motor.MWT'),
      value: isNumber(mwtDoc.meters) ? <FormattedMessage id="motor.meter" values={{ meter: mwtDoc.meters }} /> : null,
      key: 'MWT',
    });
    dmdData.push({
      date: isPartialDate(mwtDoc.date) ? formatPartialDate(mwtDoc.date) : '',
      title: fm('motor.MWT'),
      value: isNumber(mwtDoc.meters) ? <FormattedMessage id="motor.meter" values={{ meter: mwtDoc.meters }} /> : null,
      key: 'MWT',
    });
  }

  if (nsaaDoc) {
    dmdData.push({
      date: isPartialDate(nsaaDoc.date) ? formatPartialDate(nsaaDoc.date) : '',
      title: fm('motor.nsaa'),
      value: nsaa.calculateScore(nsaaDoc),
      key: 'nsaa',
    });
  }

  if (mfmDoc) {
    smaData.push({
      date: isPartialDate(mfmDoc.date) ? formatPartialDate(mfmDoc.date) : '',
      title: fm('motor.mfm'),
      value: mfm.calculateScore(mfmDoc)?.total,
      key: 'mfm',
    });
    dmdData.push({
      date: isPartialDate(mfmDoc.date) ? formatPartialDate(mfmDoc.date) : '',
      title: fm('motor.mfm'),
      value: mfm.calculateScore(mfmDoc)?.total,
      key: 'mfm',
    });
  }

  return (
    <React.Fragment>
      <PlatformConditional platform="sma">
        <TileContentMaker type="specificList" data={smaData} />
      </PlatformConditional>
      <PlatformConditional platform="dmd">
        <TileContentMaker type="specificList" data={dmdData} />
      </PlatformConditional>
    </React.Fragment>
  );
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>;
}

export default MotorDash;
